import React, { FC, memo, useMemo } from "react";
import { LayoutChangeEvent, TouchableOpacity } from "react-native";
import { View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Icon, Text } from "@lookiero/sty-psp-ui";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Footer } from "../Footer";
import { style as productDetailFooterStyle } from "./ProductDetailFooter.styles";

const ACTIVE_OPACITY = 0.6;

interface ProductDetailFooterProps {
  readonly isAvailable: boolean;
  readonly isWishlisted: boolean;
  readonly isAnySizeSelected: boolean;
  readonly onPressSoldOut: () => void;
  readonly onPressBasket: () => void;
  readonly onPressWishlist: () => void;
  readonly onLayout?: (event: LayoutChangeEvent) => void;
}

const ProductDetailFooter: FC<ProductDetailFooterProps> = ({
  isAvailable,
  isWishlisted,
  isAnySizeSelected,
  onPressSoldOut,
  onPressBasket,
  onPressWishlist,
  onLayout,
}) => {
  const productFreeReturnsText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.PRODUCT_FREE_RETURNS });
  const productStockOutCtaText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.PRODUCT_STOCK_OUT_CTA });
  const productAddToCartCtaText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.PRODUCT_ADD_TO_CART_CTA });

  const style = useMemo(() => productDetailFooterStyle(), []);

  const wishlistIconColor = !isAnySizeSelected
    ? style.colorTextMedium
    : isWishlisted
      ? style.colorBgRose
      : style.colorText;

  return (
    <Footer onLayout={onLayout}>
      <View style={style.infoRow}>
        <Text level={3} variant="detail">
          {productFreeReturnsText}
        </Text>
      </View>

      <View style={style.actionButtons}>
        {!isAvailable ? (
          <Button
            size="small"
            style={{ button: style.actionButtonSmall }}
            testID="send-email-stockOut-button"
            onPress={onPressSoldOut}
          >
            {productStockOutCtaText}
          </Button>
        ) : (
          <Button
            disabled={!isAnySizeSelected}
            size="small"
            style={{ button: style.actionButtonSmall }}
            testID="add-to-cart-button"
            onPress={onPressBasket}
          >
            {productAddToCartCtaText}
          </Button>
        )}

        <TouchableOpacity
          activeOpacity={ACTIVE_OPACITY}
          disabled={!isAnySizeSelected}
          style={style.addToWishlistButton}
          testID="button-wishlist"
          onPress={onPressWishlist}
        >
          <Icon name={isWishlisted ? "heart_fill" : "heart"} style={{ color: wishlistIconColor }} />
        </TouchableOpacity>
      </View>
    </Footer>
  );
};

const MemoizedProductDetailFooter = memo(ProductDetailFooter);

export { MemoizedProductDetailFooter as ProductDetailFooter };
